/**
 * Cart drawer product recommendations
 * Keeps desktop and mobile recommendations in sync with one fetch/render pipeline.
 */

const requestedOkendoRatings = new WeakSet();

function initializeOkendoStarRatings(root = document) {
  try {
    const reviewElements = root.querySelectorAll('[data-oke-star-rating]:not([data-oke-rendered])');

    if (!reviewElements.length) return;

    if (typeof window.okeWidgetApi?.initWidget === 'function') {
      reviewElements.forEach((element) => {
        if (!element.isConnected || requestedOkendoRatings.has(element)) return;
        requestedOkendoRatings.add(element);
        Promise.resolve().then(() => window.okeWidgetApi.initWidget(element)).catch((error) => {
          requestedOkendoRatings.delete(element);
          console.warn('Error initializing Okendo star rating:', error);
        });
      });
      return;
    }

    if (typeof window.Okendo !== 'undefined' && typeof window.Okendo.initWidget === 'function') {
      window.Okendo.initWidget();
    }
  } catch (error) {
    console.warn('Error initializing Okendo star ratings:', error);
  }
}

class CartDrawerRecommendations {
  constructor() {
    this.desktopTrack = null;
    this.mobileTrack = null;
    this.products = [];
    this.refreshTimer = null;
    this.activeRequest = null;
    this.requestId = 0;
    this.cache = new Map();
    this.lastUrl = '';
    this.lastRenderedSignature = '';
    this.mobileMediaQuery = window.matchMedia('(max-width: 989px)');

    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('cart-drawer:opened', () => this.scheduleRefresh({ delay: 0 }));
    document.addEventListener('cart-drawer:updated', (event) => {
      if (event.detail?.source === 'hot-zones') {
        this.scheduleHotZoneRefresh(event.detail.cartData);
        return;
      }

      this.scheduleRefresh({ delay: 80 });
    });
    document.addEventListener('cart:refresh', () => this.scheduleRefresh({ delay: 150 }));
    const handleViewportChange = () => this.scheduleRefresh({ delay: 0, force: true });
    if (typeof this.mobileMediaQuery.addEventListener === 'function') {
      this.mobileMediaQuery.addEventListener('change', handleViewportChange);
    } else {
      this.mobileMediaQuery.addListener(handleViewportChange);
    }
  }

  initialize() {
    this.scheduleRefresh({ delay: 0 });
  }

  reinitialize() {
    this.scheduleRefresh({ delay: 0, force: true });
  }

  scheduleRefresh({ delay = 0, force = false, attempts = 0 } = {}) {
    clearTimeout(this.refreshTimer);

    this.refreshTimer = setTimeout(() => {
      this.refresh({ force, attempts }).catch((error) => {
        console.error('Error refreshing cart drawer recommendations:', error);
      });
    }, delay);
  }

  scheduleHotZoneRefresh(cartData) {
    const nextConfig = this.getRecommendationsConfigFromCartData(cartData);
    const currentKey = this.getRecommendationsRequestKey();

    if (!nextConfig.url || this.buildRequestKey(nextConfig) === currentKey) return;

    const productRecommendationsSection = document.querySelector('.drawer__column--right .product-recommendations');
    if (productRecommendationsSection) {
      productRecommendationsSection.dataset.url = nextConfig.url;
      productRecommendationsSection.dataset.relatedUrl = nextConfig.relatedUrl;
      productRecommendationsSection.dataset.excludedProductIds = nextConfig.excludedProductIds;
    }

    this.scheduleRefresh({ delay: 80, force: true });
  }

  async refresh({ force = false, attempts = 0 } = {}) {
    return this.performRefresh({ force, attempts });
  }

  async performRefresh({ force = false, attempts = 0 } = {}) {
    if (!this.resolveTargets()) {
      if (attempts < 5 && document.querySelector('cart-drawer.active')) {
        this.scheduleRefresh({ delay: 120, force, attempts: attempts + 1 });
      }

      return;
    }

    const url = this.getRecommendationsUrl();
    if (!url) {
      if (attempts < 5 && document.querySelector('cart-drawer.active')) {
        this.scheduleRefresh({ delay: 120, force, attempts: attempts + 1 });
      }

      if (!this.hasRenderedRecommendations()) {
        this.showNoRecommendations();
      }
      return;
    }

    const relatedUrl = this.getRelatedRecommendationsUrl();
    const requestKey = this.getRecommendationsRequestKey();

    if (!force && requestKey === this.lastUrl && this.hasRenderedRecommendations()) {
      initializeOkendoStarRatings(this.desktopTrack || document);
      if (this.mobileTrack) {
        initializeOkendoStarRatings(this.mobileTrack);
      }
      this.initializeDesktopNavigation();
      return;
    }

    const cached = this.cache.get(requestKey);
    if (cached && !force) {
      this.abortActiveRequest();
      this.products = cached.products;
      this.lastUrl = requestKey;
      this.lastRenderedSignature = cached.signature;
      this.render();
      return;
    }

    this.abortActiveRequest();

    const controller = new AbortController();
    const requestId = ++this.requestId;
    this.activeRequest = controller;

    let products = [];
    try {
      const complementaryText = await this.fetchRecommendationHtml(url, controller.signal);
      products = this.extractProducts(complementaryText);

      if (products.length < 6 && relatedUrl && relatedUrl !== url) {
        const relatedText = await this.fetchRecommendationHtml(relatedUrl, controller.signal);
        products = this.mergeProducts(products, this.extractProducts(relatedText));
      }

      products = products.slice(0, 6);
    } catch (error) {
      if (error.name === 'AbortError') return;
      throw error;
    } finally {
      if (this.activeRequest === controller) {
        this.activeRequest = null;
      }
    }

    if (requestId !== this.requestId) return;

    const signature = products.map((product) => product.html).join('||');

    if (!force && signature === this.lastRenderedSignature && requestKey === this.lastUrl) {
      return;
    }

    this.cache.set(requestKey, { products, signature });
    this.products = products;
    this.lastUrl = requestKey;
    this.lastRenderedSignature = signature;
    this.render();
  }

  abortActiveRequest() {
    if (!this.activeRequest) return;

    this.activeRequest.abort();
    this.activeRequest = null;
    this.requestId += 1;
  }

  hasRenderedRecommendations() {
    const desktopHasCards = Boolean(this.desktopTrack?.querySelector('.recommendations-slide, .card-wrapper, .product-card-wrapper'));
    const mobileHasCards = Boolean(this.mobileTrack?.querySelector('.recommendations-slide, .card-wrapper, .product-card-wrapper'));
    return desktopHasCards || mobileHasCards;
  }

  resolveTargets() {
    this.desktopTrack = document.querySelector('#recommendationsTrack');
    this.mobileTrack = document.querySelector('.recommendations-track--mobile');

    return Boolean(this.desktopTrack || this.mobileTrack);
  }

  getRecommendationsUrl() {
    const productRecommendationsSection = document.querySelector('.drawer__column--right .product-recommendations');
    return productRecommendationsSection?.dataset.url || '';
  }

  getRelatedRecommendationsUrl() {
    const productRecommendationsSection = document.querySelector('.drawer__column--right .product-recommendations');
    return productRecommendationsSection?.dataset.relatedUrl || '';
  }

  getExcludedProductIds() {
    const productRecommendationsSection = document.querySelector('.drawer__column--right .product-recommendations');
    return productRecommendationsSection?.dataset.excludedProductIds || '';
  }

  buildRequestKey({ url = '', relatedUrl = '', excludedProductIds = '' } = {}) {
    return [url, relatedUrl, excludedProductIds].join('|');
  }

  getRecommendationsRequestKey() {
    return this.buildRequestKey({
      url: this.getRecommendationsUrl(),
      relatedUrl: this.getRelatedRecommendationsUrl(),
      excludedProductIds: this.getExcludedProductIds()
    });
  }

  getRecommendationsConfigFromCartData(cartData) {
    const sectionHtml = cartData?.sections?.['cart-drawer'];
    if (!sectionHtml) return {};

    const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const productRecommendationsSection = html.querySelector('.drawer__column--right .product-recommendations');
    return {
      url: productRecommendationsSection?.dataset.url || '',
      relatedUrl: productRecommendationsSection?.dataset.relatedUrl || '',
      excludedProductIds: productRecommendationsSection?.dataset.excludedProductIds || ''
    };
  }

  async fetchRecommendationHtml(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`Recommendation request failed with status ${response.status}`);
    }
    return response.text();
  }

  extractProducts(text) {
    const html = document.createElement('div');
    html.innerHTML = text;
    const excludedProductIds = new Set(
      this.getExcludedProductIds().split(',').map((id) => id.trim()).filter(Boolean)
    );

    const productList = html.querySelector('ul#originalRecommendations');
    if (productList && productList.children.length > 0) {
      return Array.from(productList.children)
        .filter((li) => !this.isExcludedMembershipRecommendation(li))
        .map((li) => ({
          id: li.dataset.productId || '',
          html: li.innerHTML
        }))
        .filter((product) => !excludedProductIds.has(product.id));
    }

    const cards = html.querySelectorAll('.card-wrapper, .product-card-wrapper');
    if (cards.length > 0) {
      return Array.from(cards)
        .filter((card) => !this.isExcludedMembershipRecommendation(card))
        .map((card) => ({
          id: card.dataset.productId || card.querySelector('a[href*="/products/"]')?.getAttribute('href') || '',
          html: card.outerHTML
        }))
        .filter((product) => !excludedProductIds.has(product.id));
    }

    return [];
  }

  isExcludedMembershipRecommendation(element) {
    const title = element?.textContent?.replace(/\s+/g, ' ').trim().toLowerCase() || '';
    return title.includes('the greenhouse membership')
      || title.includes('greenhouse member gift (digital)');
  }

  mergeProducts(primaryProducts, fallbackProducts) {
    const seen = new Set();

    return [...primaryProducts, ...fallbackProducts].filter((product) => {
      const key = product.id || product.html;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 6);
  }

  render() {
    if (this.products.length === 0) {
      this.showNoRecommendations();
      return;
    }

    const useMobileTrack = this.mobileMediaQuery.matches && this.mobileTrack;
    const activeTrack = useMobileTrack ? this.mobileTrack : (this.desktopTrack || this.mobileTrack);
    const inactiveTrack = activeTrack === this.desktopTrack ? this.mobileTrack : this.desktopTrack;

    this.clearTrack(inactiveTrack);
    this.renderTrack(activeTrack, 'recommendations-slide');

    const desktopContainer = document.querySelector('.drawer__column--right .product-recommendations');
    if (desktopContainer) {
      desktopContainer.style.display = activeTrack === this.desktopTrack ? 'flex' : 'none';
    }

    const mobileContainer = document.querySelector('cart-drawer-items .product-recommendations--mobile');
    if (mobileContainer) {
      mobileContainer.style.display = activeTrack === this.mobileTrack ? 'flex' : 'none';
    }
  }

  clearTrack(track) {
    if (!track) return;
    track.dataset.recommendationsSignature = '';
    track.innerHTML = '';
  }

  renderTrack(track, slideClassName) {
    if (!track) return;

    if (track.dataset.recommendationsSignature === this.lastRenderedSignature && track.children.length > 0) {
      initializeOkendoStarRatings(track);
      this.initializeCardVariantPickers(track);
      return;
    }

    track.innerHTML = '';
    track.dataset.recommendationsSignature = this.lastRenderedSignature;

    this.products.forEach((product) => {
      const slide = document.createElement('div');
      slide.className = slideClassName;
      slide.innerHTML = product.html;
      track.appendChild(slide);
    });

    initializeOkendoStarRatings(track);
    this.initializeCardVariantPickers(track);
    this.initializeDesktopNavigation();
  }

  initializeDesktopNavigation() {
    const container = document.querySelector('.drawer__column--right .product-recommendations');
    const slider = container?.querySelector('.recommendations-slider');
    const previousButton = container?.querySelector('.product-recommendations__control--previous');
    const nextButton = container?.querySelector('.product-recommendations__control--next');

    if (!container || !slider || !previousButton || !nextButton) return;

    const updateControls = () => {
      const maxScrollLeft = Math.max(slider.scrollWidth - slider.clientWidth, 0);
      const hasOverflow = maxScrollLeft > 2;

      container.classList.toggle('product-recommendations--scrollable', hasOverflow);
      previousButton.disabled = !hasOverflow || slider.scrollLeft <= 2;
      nextButton.disabled = !hasOverflow || slider.scrollLeft >= maxScrollLeft - 2;
    };

    if (slider.dataset.navigationBound !== 'true') {
      slider.dataset.navigationBound = 'true';
      slider.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });

      previousButton.addEventListener('click', () => {
        slider.scrollBy({ left: -Math.max(slider.clientWidth * 0.9, 260), behavior: 'smooth' });
      });

      nextButton.addEventListener('click', () => {
        slider.scrollBy({ left: Math.max(slider.clientWidth * 0.9, 260), behavior: 'smooth' });
      });
    }

    requestAnimationFrame(updateControls);
  }

  showNoRecommendations() {
    if (this.desktopTrack) {
      this.desktopTrack.dataset.recommendationsSignature = '';
      this.desktopTrack.innerHTML = '';
    }

    if (this.mobileTrack) {
      this.mobileTrack.dataset.recommendationsSignature = '';
      this.mobileTrack.innerHTML = '';
    }

    const desktopContainer = document.querySelector('.drawer__column--right .product-recommendations');
    if (desktopContainer) {
      desktopContainer.style.display = 'none';
    }

    const mobileContainer = document.querySelector('cart-drawer-items .product-recommendations--mobile');
    if (mobileContainer) {
      mobileContainer.style.display = 'none';
    }
  }

  initializeCardVariantPickers(root) {
    setTimeout(() => {
      window.cardVariantPicker?.setupPurpleDotManagedCards(root);
      window.cardVariantPicker?.setupCustomPreorderCards(root);

      window.cardVariantPicker?.initializeAllCards(root);

      window.cardVariantPicker?.setupPurpleDotManagedCards(root);
      window.cardVariantPicker?.setupCustomPreorderCards(root);
    }, 120);
  }
}

function initializeRecommendationsSliders() {
  if (!window.recommendationsSlider) {
    window.recommendationsSlider = new CartDrawerRecommendations();
  }

  if (!window.mobileRecommendationsSlider) {
    window.mobileRecommendationsSlider = window.recommendationsSlider;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeRecommendationsSliders);
} else {
  initializeRecommendationsSliders();
}
